/**
 * Intelligence API Service (v3.0)
 */

import api from './api'
import type {
  CryptoSector,
  SectorSignal,
  SectorOpportunity,
  CryptoProtocol,
  GeoOpportunity,
  MarketAlert,
  TrackSectorRequest,
  IntegrationStatus,
} from '../types/intelligence'

export const intelligenceService = {
  // Sectors
  async getSectors(params?: { stage?: string; active_only?: boolean; limit?: number }) {
    const response = await api.get<CryptoSector[]>('/intelligence/sectors', { params })
    return response.data
  },

  async getSectorOpportunities(params?: { min_score?: number; limit?: number }) {
    const response = await api.get<SectorOpportunity[]>('/intelligence/sectors/opportunities', {
      params,
    })
    return response.data
  },

  async getSectorDetail(sectorId: number) {
    const response = await api.get<CryptoSector>(`/intelligence/sectors/${sectorId}`)
    return response.data
  },

  async getSectorSignals(sectorId: number, days: number = 30) {
    const response = await api.get<SectorSignal[]>(`/intelligence/sectors/${sectorId}/signals`, {
      params: { days },
    })
    return response.data
  },

  async trackSector(sectorId: number, request: TrackSectorRequest) {
    const response = await api.post(`/intelligence/sectors/${sectorId}/track`, request)
    return response.data
  },

  async untrackSector(sectorId: number) {
    const response = await api.delete(`/intelligence/sectors/${sectorId}/track`)
    return response.data
  },

  // Protocols
  async getProtocols(params?: { sector_id?: number; blockchain?: string; limit?: number }) {
    const response = await api.get<CryptoProtocol[]>('/intelligence/protocols', { params })
    return response.data
  },

  // Geographic
  async getGeoOpportunities(limit: number = 10) {
    const response = await api.get<GeoOpportunity[]>('/intelligence/geographic/opportunities', {
      params: { limit },
    })
    return response.data
  },

  // Alerts
  async getAlerts(params?: { unread_only?: boolean; limit?: number }) {
    const response = await api.get<MarketAlert[]>('/intelligence/alerts', { params })
    return response.data
  },

  async markAlertRead(alertId: number) {
    const response = await api.patch(`/intelligence/alerts/${alertId}/read`)
    return response.data
  },

  async dismissAlert(alertId: number) {
    const response = await api.patch(`/intelligence/alerts/${alertId}/dismiss`)
    return response.data
  },

  // Integrations
  async getIntegrationStatus() {
    const response = await api.get<IntegrationStatus>('/intelligence/integrations/status')
    return response.data
  },
}

export default intelligenceService
